import { CHAT_LAYOUT } from 'extracted-chat-components/enums';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectParentViewSize } from 'selectors/selectors';

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<{width: number, height: number}>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

export function useWindowSizeDebounced() {
  const { width, height, viewState } = useSelector(selectParentViewSize);
  const { width: chatWindowWidth } = useWindowSize();
  const [windowSize, setWindowSize] = useState<{width: number, height: number, viewState: string}>({
    width,
    height,
    viewState,
  });

  useEffect(() => {
    setWindowSize({
      width,
      height,
      viewState,
    });
  }, [height, width, viewState]);

  useEffect(() => {
    if (chatWindowWidth <= 380) {
      setWindowSize((prev) => ({
        ...prev,
        viewState: CHAT_LAYOUT.ANCHORED,
      }));
    } else {
      setWindowSize((prev) => ({
        ...prev,
        viewState: CHAT_LAYOUT.FULLSCREEN,
      }));
    }
  }, [chatWindowWidth]);

  const isExpanded = useMemo(() => windowSize.viewState === CHAT_LAYOUT.FULLSCREEN, [windowSize]);
  const isMobile = useMemo(() => width <= 460, [width]);

  return { isMobile, isExpanded, ...windowSize };
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isMobileForClientHub, setIsMobileForClientHub] = useState<boolean>(false);
  const [isMobileOrTab, setIsMobileOrTab] = useState<boolean>(false);

  const { width } = useWindowSize();

  useEffect(() => {
    if (width <= 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }

    if (width <= 654) {
      setIsMobileForClientHub(true);
    } else {
      setIsMobileForClientHub(false);
    }

    if (width <= 912) {
      setIsMobileOrTab(true);
    } else {
      setIsMobileOrTab(false);
    }
  }, [width]);

  return { isMobile, isMobileForClientHub, isMobileOrTab };
}
